import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { permissions as permissionsApi } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { ShieldCheck, Save, RotateCcw, Lock } from "lucide-react";

export default function PermissionsPage() {
  const { t } = useTranslation();
  const { user, refreshPermissions } = useApp();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({}); // role -> {perm: bool}
  const [savingRole, setSavingRole] = useState(null);

  const load = async () => {
    const r = await permissionsApi.list();
    setData(r);
    const next = {};
    for (const item of r.items || []) next[item.role] = { ...item.permissions };
    setDraft(next);
  };

  useEffect(() => {
    load().catch(console.error);
  }, []);

  const dirtyRoles = useMemo(() => {
    if (!data) return new Set();
    const dirty = new Set();
    for (const item of data.items || []) {
      const cur = draft[item.role] || {};
      const orig = item.permissions || {};
      for (const k of data.permission_keys) {
        if (!!cur[k] !== !!orig[k]) {
          dirty.add(item.role);
          break;
        }
      }
    }
    return dirty;
  }, [data, draft]);

  const togglePerm = (role, perm) => {
    if (role === "admin") return; // immutable
    setDraft((d) => ({
      ...d,
      [role]: { ...(d[role] || {}), [perm]: !d[role]?.[perm] },
    }));
  };

  const saveRole = async (role) => {
    setSavingRole(role);
    try {
      await permissionsApi.patch(role, draft[role] || {});
      toast.success(t("permissions.saved", { role: t(`permissions.role_${role}`) }));
      await load();
      // If the admin edited their own role (e.g. demoted self), refresh the active permission map.
      if (user?.role === role) await refreshPermissions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSavingRole(null);
    }
  };

  const resetRole = (role) => {
    if (!data) return;
    const orig = (data.items || []).find((i) => i.role === role);
    if (!orig) return;
    setDraft((d) => ({ ...d, [role]: { ...orig.permissions } }));
  };

  if (!data) return <div className="p-10 text-stone-500">{t("common.loading")}</div>;

  const roles = data.roles;
  const keys = data.permission_keys;

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="permissions-page">
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#1E3A8A] grid place-items-center text-white">
          <ShieldCheck size={18} />
        </span>
        <div>
          <div className="canales-overline">{t("nav.permissions")}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">{t("permissions.title")}</h1>
        </div>
      </div>
      <p className="text-stone-600 mt-3 max-w-2xl text-sm">{t("permissions.subtitle")}</p>

      <Card className="canales-card mt-6 overflow-x-auto" data-testid="permissions-matrix">
        <div className="min-w-[760px]">
          {/* Header row: roles */}
          <div className="grid items-center gap-2 px-5 py-3 border-b border-stone-200 bg-stone-50/60" style={{ gridTemplateColumns: `minmax(280px, 1fr) repeat(${roles.length}, 110px)` }}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-stone-500 font-bold">{t("permissions.permission")}</div>
            {roles.map((role) => (
              <div key={role} className="text-center">
                <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-bold">
                  {t(`permissions.role_${role}`)}
                </div>
                {role === "admin" && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-stone-400">
                    <Lock size={10} /> {t("permissions.immutable")}
                  </div>
                )}
              </div>
            ))}
          </div>

          {keys.map((key, idx) => (
            <div
              key={key}
              className={`grid items-center gap-2 px-5 py-3 ${idx % 2 === 0 ? "bg-white" : "bg-stone-50/40"}`}
              style={{ gridTemplateColumns: `minmax(280px, 1fr) repeat(${roles.length}, 110px)` }}
              data-testid={`perm-row-${key}`}
            >
              <div>
                <div className="text-sm font-semibold">{t(`permissions.k_${key}`)}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">{t(`permissions.d_${key}`, "")}</div>
              </div>
              {roles.map((role) => {
                const val = !!(draft[role] || {})[key];
                const isAdmin = role === "admin";
                return (
                  <div key={role} className="flex justify-center">
                    <Checkbox
                      checked={isAdmin ? true : val}
                      disabled={isAdmin}
                      onCheckedChange={() => togglePerm(role, key)}
                      data-testid={`perm-${role}-${key}`}
                    />
                  </div>
                );
              })}
            </div>
          ))}

          <div className="grid items-center gap-2 px-5 py-4 border-t border-stone-200 bg-stone-50/60" style={{ gridTemplateColumns: `minmax(280px, 1fr) repeat(${roles.length}, 110px)` }}>
            <div className="text-xs text-stone-500">{t("permissions.save_per_role_hint")}</div>
            {roles.map((role) => {
              const isAdmin = role === "admin";
              const isDirty = dirtyRoles.has(role);
              return (
                <div key={role} className="flex justify-center gap-1">
                  {!isAdmin && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetRole(role)}
                        disabled={!isDirty}
                        title={t("permissions.reset")}
                        data-testid={`perm-reset-${role}`}
                      >
                        <RotateCcw size={12} />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => saveRole(role)}
                        disabled={!isDirty || savingRole === role}
                        className="brand-button gap-1.5"
                        data-testid={`perm-save-${role}`}
                      >
                        <Save size={12} />
                        {savingRole === role ? "…" : t("common.save")}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
